import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { hasPermission } from '../../utils/permissions';

import { API_BASE_URL } from '../../config/api';

function RouteManagement({ onNavigate, onSelectRoute }) {
  const { user } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [journeys, setJourneys] = useState([]);
  const [filterJourney, setFilterJourney] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDropdowns();
  }, []);

  useEffect(() => {
    fetchRoutes();
  }, [filterJourney]);

  const fetchDropdowns = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE_URL}/journeys`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setJourneys(await res.json());
      }
    } catch (e) {
      console.error('Failed to fetch journeys dropdown', e);
    }
  };

  const fetchRoutes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const url = filterJourney
        ? `${API_BASE_URL}/routes?journey_id=${encodeURIComponent(filterJourney)}`
        : `${API_BASE_URL}/routes`;
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setRoutes(await res.json());
      }
    } catch (e) {
      console.error('Failed to fetch routes', e);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this route? This will delete all its waypoints.")) return;
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE_URL}/routes/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchRoutes();
      } else {
        alert("Failed to delete route");
      }
    } catch (e) {
      console.error('Failed to delete route', e);
    }
  };

  const getJourneyName = (journeyId) => {
    if (!journeyId) return 'N/A';
    const journey = journeys.find(j => j.id === journeyId);
    return journey ? journey.name : 'Unknown Journey';
  };

  const getBadgeClass = (type) => {
    switch (type) {
      case 'TRADITIONAL': return 'badge-success';
      case 'VIP': return 'badge-danger';
      case 'EMERGENCY': return 'badge-danger';
      default: return 'badge-info';
    }
  };

  // Roles permission check
  const canCreate = hasPermission(user, 'route.create');
  const canEdit = hasPermission(user, 'route.edit');
  const canDelete = hasPermission(user, 'route.delete');
  const canView = hasPermission(user, 'route.view');

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', gap: '12px', flexWrap: 'wrap' }}>
        <h3>Yatra Routes Directory</h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          <select
            value={filterJourney}
            onChange={(e) => setFilterJourney(e.target.value)}
            style={{ padding: '8px', borderRadius: '6px', border: '1px solid #ddd' }}
          >
            <option value="">All Journeys</option>
            {journeys.map(j => (
              <option key={j.id} value={j.id}>{j.name}</option>
            ))}
          </select>
          {canCreate && (
            <button className="btn-primary" onClick={() => onNavigate('route-create')}>
              + Add Route
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <p>Loading routes...</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Route Name</th>
              <th>Type</th>
              <th>Journey</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {routes.map((r) => (
              <tr key={r.id}>
                <td>{r.name}</td>
                <td><span className={getBadgeClass(r.route_type)}>{r.route_type || 'TRADITIONAL'}</span></td>
                <td>{getJourneyName(r.journey_id)}</td>
                <td>
                  <span className={r.active ? 'badge-success' : 'badge-danger'} style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>
                    {r.active ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                </td>
                <td>
                  {canView && (
                    <button 
                      className="btn-outline btn-small" 
                      style={{ marginRight: '8px', padding: '4px 8px', fontSize: '12px' }}
                      onClick={() => {
                        onSelectRoute(r.id);
                        onNavigate('route-view');
                      }}
                    >
                      View
                    </button>
                  )}
                  {canEdit && (
                    <button 
                      className="btn-outline btn-small" 
                      style={{ marginRight: '8px', padding: '4px 8px', fontSize: '12px' }}
                      onClick={() => {
                        onSelectRoute(r.id);
                        onNavigate('route-edit');
                      }}
                    >
                      Edit / Waypoints
                    </button>
                  )}
                  {canDelete && (
                    <button 
                      className="btn-primary btn-small" 
                      style={{ backgroundColor: '#f44336', padding: '4px 8px', fontSize: '12px' }}
                      onClick={() => handleDelete(r.id)}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {routes.length === 0 && (
              <tr><td colSpan="5" style={{ textAlign: 'center', padding: '20px' }}>No routes found.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default RouteManagement;
